if (!customElements.get('mix-and-match')) {
  customElements.define(
    'mix-and-match',
    class MixAndMatch extends HTMLElement {
      constructor() {
        super();

        this.triggers = Array.from(this.querySelectorAll('.mix-and-match__trigger'));
        this.activeTrigger = this.triggers[this.triggers.length - 1];
        this.dialog = this.querySelector('.mix-and-match__dialog');
        this.content = this.querySelector('[data-mix-and-match-content]');
        this.loadingMarkup = this.content.innerHTML;
        this.loaded = false;

        this.triggers.forEach((trigger) => {
          trigger.addEventListener('click', this.onTriggerClick.bind(this));
        });
        this.selectTrigger(this.activeTrigger);
        this.querySelector('[data-mix-and-match-close]').addEventListener('click', this.close.bind(this));
        this.dialog.addEventListener('click', this.onDialogClick.bind(this));
        this.dialog.addEventListener('close', this.onClose.bind(this));
        this.content.addEventListener('click', this.onContentClick.bind(this));
      }

      connectedCallback() {
        // The dialog is moved out of the product info container so that
        // product page styles don't override the shared product card styles.
        if (this.dialog.parentElement !== document.body) {
          document.body.appendChild(this.dialog);
        }
      }

      disconnectedCallback() {
        this.dialog.remove();
      }

      onTriggerClick(event) {
        const trigger = event.currentTarget;

        this.selectTrigger(trigger);
        if (this.dataset.syncTieredPricing !== 'false' && !trigger.closest('tiered-pricing-tabs')) {
          this.syncTieredPricing(trigger.dataset.quantity, trigger.dataset.price);
        }
        this.open();
      }

      selectTrigger(selectedTrigger) {
        if (!selectedTrigger) {
          return;
        }

        for (const trigger of this.triggers) {
          const isActive = trigger === selectedTrigger;

          if (trigger.classList.contains('tiered-pricing-tab')) {
            trigger.classList.toggle('tiered-pricing-tab--active', isActive);
          }
          if (trigger.hasAttribute('aria-pressed')) {
            trigger.setAttribute('aria-pressed', isActive.toString());
          }
        }

        this.activeTrigger = selectedTrigger;
      }

      syncTieredPricing(quantity, price) {
        const productInformation = this.closest('.product__info-container');
        const searchRoot = productInformation || document;
        const tieredPricing = searchRoot.querySelector('tiered-pricing-tabs');

        if (typeof tieredPricing?.selectTier === 'function') {
          tieredPricing.selectTier(Number.parseInt(quantity, 10), Number.parseFloat(price));
          return;
        }

        const tieredPricingTabs = Array.from(
          searchRoot.querySelectorAll('tiered-pricing-tabs .tiered-pricing-tab'),
        );
        const matchingTier = tieredPricingTabs.find((tab) => tab.dataset.quantity === quantity);

        if (matchingTier) {
          matchingTier.click();
          return;
        }

        const quantityInputs = searchRoot.querySelectorAll('.quantity__input[id^="Quantity-"]');

        for (const quantityInput of quantityInputs) {
          quantityInput.value = quantity;
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      open() {
        this.dialog.showModal();
        document.body.classList.add('mix-and-match-open');

        if (!this.loaded) {
          this.loadProducts();
        }
      }

      close() {
        this.dialog.close();
      }

      onClose() {
        document.body.classList.remove('mix-and-match-open');
        this.activeTrigger?.focus();
      }

      onDialogClick(event) {
        if (event.target === this.dialog) {
          this.close();
        }
      }

      onContentClick(event) {
        const decreaseButton = event.target.closest('.quantity-minus');
        const increaseButton = event.target.closest('.quantity-plus');

        if (!decreaseButton && !increaseButton) {
          return;
        }

        const productCard = event.target.closest('.card-information');
        const quantityInput = productCard?.querySelector('.quantity-wrapper input');

        if (!quantityInput) {
          return;
        }

        const currentQuantity = Number.parseInt(quantityInput.value, 10) || 1;
        const minimumQuantity = Number.parseInt(quantityInput.min, 10) || 1;

        quantityInput.value = decreaseButton
          ? Math.max(minimumQuantity, currentQuantity - 1)
          : currentQuantity + 1;
      }

      async loadProducts() {
        this.content.innerHTML = this.loadingMarkup;

        try {
          const searchUrl = new URL(this.dataset.searchUrl, window.location.origin);
          const escapedTag = this.dataset.tag.replaceAll('"', '\\"');

          searchUrl.searchParams.set('view', 'mix-and-match');
          searchUrl.searchParams.set('type', 'product');
          searchUrl.searchParams.set('options[prefix]', 'last');
          searchUrl.searchParams.set('q', `tag:"${escapedTag}"`);

          const response = await fetch(searchUrl.toString(), {
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
            },
          });

          if (!response.ok) {
            throw new Error(`Mix and match search failed with status ${response.status}`);
          }

          this.content.innerHTML = await response.text();
          this.loaded = true;
        } catch (error) {
          const errorMessage = document.createElement('p');
          errorMessage.className = 'mix-and-match__error';
          errorMessage.setAttribute('role', 'alert');
          errorMessage.textContent = this.dataset.errorMessage;
          this.content.replaceChildren(errorMessage);
          console.error(error);
        }
      }
    }
  );
}
